import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';

/** Why a track was hated. Keys must match HATE_REASONS in the backend route. */
export type HateReason = 'repetitive' | 'screamy' | 'too_clubby' | 'too_whiny' | 'just_dont_like';

/** Ratings the app writes now. Older rows may hold -2 or +2; treat sign as like/hate. */
export const LIKE_RATING = 1;
export const HATE_RATING = -1;

export interface TrackRating {
  trackId: string;
  trackName: string;
  artistName: string;
  albumName: string | null;
  rating: number; // > 0 like, < 0 hate (legacy rows use -2..+2)
  reason: HateReason | null;
  ratedAt: string;
}

export interface RateTrackInput {
  trackId: string;
  trackName: string;
  artistName: string;
  albumName?: string;
  rating: number;
  reason?: HateReason;
}

@Injectable({ providedIn: 'root' })
export class TrackRatingService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/track-rating`;

  rateTrack(input: RateTrackInput): Observable<void> {
    return this.http
      .put<{ success: boolean }>(this.baseUrl, input, { withCredentials: true })
      .pipe(map(() => undefined));
  }

  getRating(trackId: string): Observable<TrackRating | null> {
    return this.http
      .get<{ success: boolean; rating: TrackRating | null }>(
        `${this.baseUrl}/${encodeURIComponent(trackId)}`,
        { withCredentials: true }
      )
      .pipe(map(response => response.rating));
  }

  getRatings(limit = 500): Observable<TrackRating[]> {
    return this.http
      .get<{ success: boolean; ratings: TrackRating[] }>(
        `${this.baseUrl}?limit=${limit}`,
        { withCredentials: true }
      )
      .pipe(map(response => response.ratings));
  }
}
